import React, { useState, useEffect } from 'react';
import { X, Copy, Share2, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LottoDraw, PredictionSet } from '../types';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  latestDraw: LottoDraw | null;
  predictions: PredictionSet[];
  initialSelection?: Set<string> | null;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, latestDraw, predictions, initialSelection }) => {
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  
  // State to track selected items to include in the share message
  // Keys: 'latest' | 'pred-{index}'
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Initialize selections when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialSelection) {
        setSelectedItems(new Set(initialSelection));
      } else {
        const initial = new Set<string>();
        if (latestDraw) initial.add('latest');
        predictions.forEach((_, idx) => initial.add(`pred-${idx}`));
        setSelectedItems(initial);
      }
    }
  }, [isOpen, latestDraw, predictions, initialSelection]);

  // Update message when selections change
  useEffect(() => {
    if (!isOpen) return;

    let generatedMsg = "🎱 *TT Lotto Plus Predictor* 🎱\n\n";
    let hasContent = false;
    
    if (selectedItems.has('latest') && latestDraw) {
      generatedMsg += `📅 Latest Draw (${latestDraw.date}):\nNumbers: ${latestDraw.numbers.join('-')} | PB: ${latestDraw.powerball}\n`;
      if (latestDraw.drawNumber) generatedMsg += `Draw #${latestDraw.drawNumber}\n`;
      generatedMsg += "\n";
      hasContent = true;
    }

    const selectedPreds = predictions
      .map((p, idx) => ({ data: p, originalIndex: idx }))
      .filter(({ originalIndex }) => selectedItems.has(`pred-${originalIndex}`));

    if (selectedPreds.length > 0) {
      generatedMsg += "🔮 My Generated Number Sets:\n";
      selectedPreds.forEach(({ data, originalIndex }) => {
         generatedMsg += `Set ${originalIndex + 1} (${data.strategy}): ${data.numbers.join('-')} [PB: ${data.powerball}]\n`;
      });
      generatedMsg += "\n";
      hasContent = true;
    }

    if (hasContent) {
        generatedMsg += "Generate your own at: [App URL]"; // Placeholder for actual URL if hosted
        setMessage(generatedMsg);
    } else {
        setMessage("Select items to generate a message...");
    }
    
    setCopied(false);
  }, [selectedItems, latestDraw, predictions, isOpen]);

  const toggleItem = (key: string) => {
    const newSet = new Set(selectedItems);
    if (newSet.has(key)) {
        newSet.delete(key);
    } else {
        newSet.add(key);
    }
    setSelectedItems(newSet);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'TT Lotto Plus Predictions',
          text: message,
        });
      } catch (err: any) {
        // Ignore AbortError which happens when user cancels the share dialog
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
        />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
        >
          <div className="flex justify-between items-center p-4 border-b border-slate-700 shrink-0">
            <h3 className="text-lg font-bold text-white font-['Space_Grotesk']">Share Results</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 overflow-y-auto custom-scrollbar">
            
            {/* Selection Toggles */}
            <div className="mb-6 space-y-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Select items to include</p>
                
                {latestDraw ? (
                    <div 
                        onClick={() => toggleItem('latest')}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedItems.has('latest') 
                            ? 'bg-slate-700/50 border-yellow-500/50' 
                            : 'bg-slate-900/30 border-slate-700 hover:border-slate-600'
                        }`}
                    >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                            selectedItems.has('latest') ? 'bg-yellow-500 border-yellow-500 text-slate-900' : 'border-slate-500 bg-slate-800'
                        }`}>
                            {selectedItems.has('latest') && <Check size={14} strokeWidth={3} />}
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-medium text-slate-200">Latest Results</div>
                            <div className="text-xs text-slate-500">
                                {latestDraw.date} {latestDraw.drawNumber ? `(#${latestDraw.drawNumber})` : ''}
                            </div>
                        </div>
                    </div>
                ) : null}

                {predictions.map((pred, idx) => (
                    <div 
                        key={idx}
                        onClick={() => toggleItem(`pred-${idx}`)}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedItems.has(`pred-${idx}`) 
                            ? 'bg-slate-700/50 border-yellow-500/50' 
                            : 'bg-slate-900/30 border-slate-700 hover:border-slate-600'
                        }`}
                    >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                            selectedItems.has(`pred-${idx}`) ? 'bg-yellow-500 border-yellow-500 text-slate-900' : 'border-slate-500 bg-slate-800'
                        }`}>
                            {selectedItems.has(`pred-${idx}`) && <Check size={14} strokeWidth={3} />}
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-medium text-slate-200">Prediction Set {idx + 1}</div>
                            <div className="text-xs text-slate-500">{pred.strategy}</div>
                        </div>
                    </div>
                ))}
                
                {predictions.length === 0 && !latestDraw && (
                    <p className="text-sm text-slate-500 italic">No content available to share.</p>
                )}
            </div>

            <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Preview Message</p>
                <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full h-32 bg-slate-900 border border-slate-700 rounded-lg p-3 text-slate-300 text-sm focus:outline-none focus:border-yellow-500/50 resize-none font-mono"
                placeholder="Message preview..."
                />
            </div>
          </div>

          <div className="p-4 bg-slate-900/50 border-t border-slate-700 flex gap-3 shrink-0">
            <button 
              onClick={handleCopy}
              className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
              {copied ? 'Copied!' : 'Copy Text'}
            </button>
            
            {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
              <button 
                onClick={handleNativeShare}
                className="flex-1 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-slate-900 font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-yellow-500/20"
              >
                <Share2 size={18} />
                Share
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ShareModal;